import { useEffect, useState } from "react";
import AxiosClient from "../Network/AxiosClient";
import { geoHierarchy } from "../Components/KPIDashboard/utils/kpiDashboardHelper";
import { buildGetApiUrl } from "./helpers";

const buildHierarchyUrl = (endpoint, params = {}) => {
  return buildGetApiUrl(`/api/organizations/api/v1/${endpoint}`, params);
};

export const fetchHierarchyData = async (endpoint, params = {}) => {
  const url = buildHierarchyUrl(endpoint, params);
  const res = await AxiosClient.get(url);
  return res?.data?.data || [];
};

export const fetchClusters = () => fetchHierarchyData("clusters");

export const fetchRegions = (geoFilters = {}) => {
  const params = {};
  if (geoFilters.circle && geoFilters.circle.length > 0) {
    params.clusters = geoFilters.circle;
  }
  return fetchHierarchyData("regions", params);
};

export const fetchAreas = (geoFilters = {}) => {
  const params = {};
  if (geoFilters.zone && geoFilters.zone.length > 0) {
    params.region = geoFilters.zone;
  } else if (geoFilters.circle && geoFilters.circle.length > 0) {
    params.clusters = geoFilters.circle;
  }
  return fetchHierarchyData("areas", params);
};

export const fetchTerritories = (geoFilters = {}) => {
  const params = {};
  if (geoFilters.area && geoFilters.area.length > 0) {
    params.area = geoFilters.area;
  } else if (geoFilters.zone && geoFilters.zone.length > 0) {
    params.region = geoFilters.zone;
  } else if (geoFilters.circle && geoFilters.circle.length > 0) {
    params.clusters = geoFilters.circle;
  }
  return fetchHierarchyData("territories", params);
};

export const fetchAgency = (geoFilters = {}) => {
  const params = {};
  if (geoFilters.territory && geoFilters.territory.length > 0) {
    params.territory = geoFilters.territory;
  } else if (geoFilters.area && geoFilters.area.length > 0) {
    params.area = geoFilters.area;
  } else if (geoFilters.zone && geoFilters.zone.length > 0) {
    params.region = geoFilters.zone;
  } else if (geoFilters.circle && geoFilters.circle.length > 0) {
    params.clusters = geoFilters.circle;
  }
  return fetchHierarchyData("agency", params);
};

const useGeoData = (selectedGeoEntity, geoFilters) => {
  const shouldFetchClusters =
    selectedGeoEntity && geoHierarchy[selectedGeoEntity] > 1;
  const shouldFetchRegions =
    selectedGeoEntity && geoHierarchy[selectedGeoEntity] > 2;
  const shouldFetchAreas =
    selectedGeoEntity && geoHierarchy[selectedGeoEntity] > 3;
  const shouldFetchTerritories =
    selectedGeoEntity && geoHierarchy[selectedGeoEntity] > 4;
  const shouldFetchAgency =
    selectedGeoEntity && geoHierarchy[selectedGeoEntity] > 5;

  const [clusterOptions, setClusterOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);

  useEffect(() => {
    if (!shouldFetchClusters) {
      setClusterOptions([]);
      return;
    }
    fetchClusters().then((data) => setClusterOptions(data));
  }, [shouldFetchClusters]);

  useEffect(() => {
    if (!shouldFetchRegions) {
      setZoneOptions([]);
      return;
    }
    fetchRegions({ circle: geoFilters.circle }).then((data) =>
      setZoneOptions(data)
    );
  }, [shouldFetchRegions, geoFilters.circle]);

  useEffect(() => {
    if (!shouldFetchAreas) {
      setAreaOptions([]);
      return;
    }
    fetchAreas({
      zone: geoFilters.zone,
      circle: geoFilters.circle,
    }).then((data) => setAreaOptions(data));
  }, [shouldFetchAreas, geoFilters.zone, geoFilters.circle]);

  useEffect(() => {
    if (!shouldFetchTerritories) {
      setTerritoryOptions([]);
      return;
    }
    fetchTerritories({
      area: geoFilters.area,
      zone: geoFilters.zone,
      circle: geoFilters.circle,
    }).then((data) => setTerritoryOptions(data));
  }, [
    shouldFetchTerritories,
    geoFilters.area,
    geoFilters.zone,
    geoFilters.circle,
  ]);

  useEffect(() => {
    if (!shouldFetchAgency) {
      setAgencyOptions([]);
      return;
    }
    fetchAgency({
      territory: geoFilters.territory,
      area: geoFilters.area,
      zone: geoFilters.zone,
      circle: geoFilters.circle,
    }).then((data) => setAgencyOptions(data));
  }, [
    shouldFetchAgency,
    geoFilters.territory,
    geoFilters.area,
    geoFilters.zone,
    geoFilters.circle,
  ]);

  return {
    clusterOptions,
    zoneOptions,
    areaOptions,
    territoryOptions,
    agencyOptions,
  };
};

export default useGeoData;
